import {
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import * as React from 'react';

import {
  Logomark,
  LogomarkSize,
  LogomarkVariant,
} from '@/components/brand/Logomark/Logomark';
import { COLORS } from '@/styles/tokens/colors';

import { DisclaimerFooter, DisclaimerFooterProps } from './DisclaimerFooter';
import { useSlideFooterBranding } from './SlideFooterBranding.context';

/**
 * Sizing for the standard slide-top heading: Title/Serif/s — 20px font /
 * 28px line-height, Romie Bold. Pinned at the call site (not on the
 * h2_display variant) because `h2_display` in
 * PresentationTypographyProvider is sized for full section headers
 * (xxl/l) — slide-top headings are smaller. The variant still
 * contributes Romie + bold weight.
 */
export const SLIDE_TOP_HEADING_SIZING_SX: SystemStyleObject<Theme> = {
  fontSize: '1.25rem', // 20px
  lineHeight: '1.75rem', // 28px
};

/**
 * Sizing for in-slide section labels — content-area headings that sit
 * below a slide-top heading (e.g. "Diagram", "Summary", "Tax Overview",
 * "Comparison", "(cont.)" suffixes). Matches the pre-Romie h2_display
 * size (18/22) so layouts that were tuned around the original Playfair
 * h2_display keep their proportions.
 */
const SLIDE_SECTION_HEADING_SIZING_SX: SystemStyleObject<Theme> = {
  fontSize: '1.125rem', // 18px
  lineHeight: '1.375rem', // 22px
};

export type SlideHeadingProps = Omit<TypographyProps, 'variant'>;

function mergeSxWithBase(
  base: SystemStyleObject<Theme>,
  sx: SlideHeadingProps['sx']
): SxProps<Theme> {
  // MUI's sx accepts an object, a callback, or an array. Pass the base
  // sizing alongside whatever the caller provided in an array so both
  // apply without losing theme-aware callbacks.
  if (!sx) return base;
  // Build a ReadonlyArray of sx items (the array variant of SxProps)
  // — typed explicitly so TS narrows the items to the
  // boolean|SystemStyleObject|callback union without widening to a
  // looser type that no longer satisfies SxProps.
  const items: ReadonlyArray<
    | boolean
    | SystemStyleObject<Theme>
    | ((theme: Theme) => SystemStyleObject<Theme>)
  > = Array.isArray(sx) ? [base, ...sx] : [base, sx];
  return items;
}

/**
 * The standard in-slide heading. Renders as Romie Bold at 20/24 — the
 * same visual height as `MainHeading`'s slide-top title. Use this for
 * a slide's primary heading when `MainHeading` doesn't fit (e.g. the
 * Table of Contents title, the PresentationDiagramSlide page name).
 * For section labels rendered *below* a slide-top heading, prefer
 * `SlideSectionHeading`.
 *
 * Uses the `h1_display` variant so the rendered element is `<h1>` (the
 * variant gives us the semantic h1 element; the sx pins the rendered
 * size to 20/24 so it doesn't pick up the variant's xxl 48/62 default).
 */
export function SlideHeading({ sx, ...rest }: SlideHeadingProps) {
  return (
    <Typography
      variant="h1_display"
      sx={mergeSxWithBase(SLIDE_TOP_HEADING_SIZING_SX, sx)}
      {...rest}
    />
  );
}

/**
 * Smaller in-slide section heading at 18/22 Romie Bold. Use for content
 * labels nested below the slide-top heading (e.g. "Diagram",
 * "Summary", "Tax Overview", "Comparison", "(cont.)" suffixes) so they
 * don't compete visually with the main title. Renders as `<h2>` via the
 * `h2_display` variant so the heading outline keeps a clean h1 → h2
 * relationship with `SlideHeading`/`MainHeading` above it.
 */
export function SlideSectionHeading({ sx, ...rest }: SlideHeadingProps) {
  return (
    <Typography
      variant="h2_display"
      sx={mergeSxWithBase(SLIDE_SECTION_HEADING_SIZING_SX, sx)}
      {...rest}
    />
  );
}

interface MainHeadingProps {
  heading: React.ReactNode;
  subheading?: React.ReactNode;
}

export function MainHeading({ heading, subheading }: MainHeadingProps) {
  return (
    <Stack>
      <SlideHeading sx={(t) => ({ color: t.palette.primary.main })}>
        {heading}
      </SlideHeading>
      <Typography
        sx={(t) => ({
          color: COLORS.NEUTRAL_GRAY[500],
          fontWeight: t.typography.fontWeightRegular,
        })}
        variant="title-xs"
      >
        {subheading}
      </Typography>
    </Stack>
  );
}

export interface SecondaryHeadingProps {
  lineOne: React.ReactNode;
  lineTwo?: React.ReactNode;
}

export function SecondaryHeading({ lineOne, lineTwo }: SecondaryHeadingProps) {
  return (
    <Stack sx={{ textAlign: 'right' }}>
      <Typography
        component="div"
        sx={{
          color: COLORS.NEUTRAL_GRAY[500],
        }}
        variant="title-xs"
      >
        {lineOne}
      </Typography>
      <Typography
        component="div"
        sx={(theme) => ({
          color: COLORS.NEUTRAL_GRAY[500],
          fontWeight: theme.typography.fontWeightRegular,
        })}
        variant="title-xs"
      >
        {lineTwo}
      </Typography>
    </Stack>
  );
}

interface FooterProps {
  hideDisclaimer?: boolean;
  slideId?: string;
  disclaimerVariant?: DisclaimerFooterProps['variant'];
}

export function Footer({
  hideDisclaimer = false,
  disclaimerVariant = 'standard',
}: FooterProps) {
  const { footerColor, useDarkLogo } = useSlideFooterBranding();
  return (
    <Box sx={{ px: 2 }}>
      <Stack
        sx={{
          borderTop: `solid ${footerColor || COLORS.GRAY[200]} 1px`,
          pt: 2,
        }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Logomark
          style={{ maxHeight: 40, width: 'auto' }}
          size={LogomarkSize.Wide}
          variant={
            useDarkLogo ? LogomarkVariant.Secondary : LogomarkVariant.Primary
          }
        />
        <Box sx={{ width: 700, textAlign: 'right' }}>
          {!hideDisclaimer && (
            <DisclaimerFooter
              variant={disclaimerVariant}
              contentSx={footerColor ? { color: footerColor } : undefined}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}
